import { useState, useMemo, useEffect } from 'react';
import { usePermissions, handlePermissionClick } from '../../hooks/usePermissions';
import { useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Clock, LayoutGrid, List, Search, PowerOff, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { DataToolbar } from '../../components/ui/DataToolbar';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { RowActionMenu } from '../../components/ui/RowActionMenu';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { hasPermission } from '../../auth/permissions';
import type { Turno, TipoSlotHorario } from '../../types/models';
import { RegistroMeta } from '../../components/admin/registro-meta';
import { TurnosFormModal } from '../../components/forms/TurnosFormModal';
import { TurnosPresetPicker } from '../../components/forms/TurnosPresetPicker';
import {
  DEFAULT_FORM,
  DEFAULT_TEMPLATE,
  DIAS_OPTIONS,
  TURNO_PRESETS,
  addMinutes,
  type TemplateSlot,
} from './turnos-shared';
import '../../components/ui/datatable.css';
import './turnos-modal.css';
import { ROUTES } from '../../api/routes';

const getTurnoVisual = (nome: string) => {
  const n = (nome || '').toLowerCase();
  if (n.includes('manhã') || n.includes('manha') || n.includes('morning') || n.includes('matutino')) {
    return { emoji: '☀️', color: '#f59e0b', bg: '#fef3c7' };
  }
  if (n.includes('tarde') || n.includes('afternoon') || n.includes('vespertino')) {
    return { emoji: '⛅', color: '#f97316', bg: '#ffedd5' };
  }
  if (n.includes('noite') || n.includes('night') || n.includes('noturno')) {
    return { emoji: '🌙', color: '#3b82f6', bg: '#dbeafe' };
  }
  return { emoji: '🕒', color: '#6366f1', bg: '#e0e7ff' };
};

export function TurnosPage() {
  const { canManage: canManageFn } = usePermissions();
  const canManage = canManageFn('turnos');

  const { data: turnos, isLoading, error: loadError, create, update, deactivate } = useCrud<Turno>('turnos');
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const escolaId = user?.escolaId;
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Turno | null>(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [template, setTemplate] = useState<TemplateSlot[]>(DEFAULT_TEMPLATE);
  const [modalStep, setModalStep] = useState<'preset' | 'form'>('preset');
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(12);
  const [statusConfirm, setStatusConfirm] = useState<{ id: string; nome: string; ativo: boolean } | null>(null);
  const [savingTurno, setSavingTurno] = useState(false);

  const turnosFiltrados = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return turnos;
    return turnos.filter((t) =>
      [t.nome, t.horaEntradaOficial, t.horaSaidaOficial, t.horaAberturaPortoes, t.horaFechamentoPortoes]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [turnos, searchQuery]);

  useEffect(() => {
    if (loadError) {
      toast.error('Não foi possível carregar os turnos. Tente atualizar a página.');
    }
  }, [loadError]);

  const openNew = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setTemplate(DEFAULT_TEMPLATE);
    setModalStep('preset');
    setModalOpen(true);
  };

  const selectPreset = (key: string) => {
    const preset = TURNO_PRESETS[key];
    setForm({ ...preset.form });
    setTemplate([...preset.template]);
    setModalStep('form');
  };

  const openEdit = (t: Turno) => {
    setEditing(t);
    setForm({
      nome: t.nome,
      horaAberturaPortoes: t.horaAberturaPortoes || '06:40',
      horaEntradaOficial: t.horaEntradaOficial || '07:00',
      toleranciaAtrasoMin: t.toleranciaAtrasoMin ?? 15,
      horaSaidaOficial: t.horaSaidaOficial || '12:00',
      horaFechamentoPortoes: t.horaFechamentoPortoes || '12:30',
      diasFuncionamento: t.diasFuncionamento?.length ? t.diasFuncionamento : DEFAULT_FORM.diasFuncionamento,
    });
    setTemplate(
      t.horariosTemplate?.length
        ? t.horariosTemplate.map((h) => ({
            numeroAula: h.numeroAula,
            horaInicio: h.horaInicio,
            horaFim: h.horaFim,
            tipo: h.tipo,
            descricao: h.descricao || '',
          }))
        : DEFAULT_TEMPLATE,
    );
    setModalStep('form');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) {
      toast.error('Informe o nome do turno');
      return;
    }

    const foraRange = template.find(
      (s) => s.horaInicio < form.horaEntradaOficial || s.horaFim > form.horaSaidaOficial,
    );
    if (foraRange) {
      toast.error(
        `O slot "${foraRange.descricao}" (${foraRange.horaInicio}-${foraRange.horaFim}) está fora do período oficial (${form.horaEntradaOficial}-${form.horaSaidaOficial}).`,
      );
      return;
    }

    for (let i = 0; i < template.length; i++) {
      for (let j = i + 1; j < template.length; j++) {
        const a = template[i];
        const b = template[j];
        if (a.horaInicio < b.horaFim && a.horaFim > b.horaInicio) {
          toast.error(
            `Conflito de horário: "${a.descricao}" (${a.horaInicio}-${a.horaFim}) sobrepõe "${b.descricao}" (${b.horaInicio}-${b.horaFim}).`,
          );
          return;
        }
      }
    }

    const invertido = template.find((s) => s.horaInicio >= s.horaFim);
    if (invertido) {
      toast.error(
        `O slot "${invertido.descricao}" tem horário de início (${invertido.horaInicio}) igual ou posterior ao fim (${invertido.horaFim}).`,
      );
      return;
    }

    const { ...turnoData } = form;
    const templateSlots = template.map((s, i) => ({ ...s, numeroAula: i + 1 }));

    setSavingTurno(true);
    try {
      let turnoId: string;

      if (editing) {
        await update.mutateAsync({ id: editing.id, ...turnoData } as Parameters<typeof update.mutateAsync>[0]);
        turnoId = editing.id;
        toast.success('Turno atualizado!');
      } else {
        const { data: created } = await api.post(ROUTES.turnos.listar(escolaId!), turnoData);
        turnoId = created.id;
        toast.success('Turno criado!');
      }

      if (templateSlots.length > 0 && turnoId) {
        try {
          await api.post(ROUTES.horarios.template(escolaId!, turnoId), { slots: templateSlots });
        } catch {
          toast.warning('Turno salvo, mas houve erro ao salvar template de horários.');
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['turnos'] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      setModalOpen(false);
    } catch (err: unknown) {
      const apiMsg =
        (err as { response?: { data?: { error?: { message?: string }; message?: string } } })?.response?.data
          ?.error?.message ??
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof apiMsg === 'string' && apiMsg ? apiMsg : 'Erro ao salvar turno');
    } finally {
      setSavingTurno(false);
    }
  };

  const requestToggleStatus = (t: Turno) => {
    setStatusConfirm({ id: t.id, nome: t.nome, ativo: t.ativo });
  };

  const confirmToggleStatus = async () => {
    if (!statusConfirm) return;
    try {
      if (statusConfirm.ativo) {
        await deactivate.mutateAsync(statusConfirm.id);
        toast.success('Turno desativado');
      } else {
        await update.mutateAsync({ id: statusConfirm.id, ativo: true } as Parameters<typeof update.mutateAsync>[0]);
        toast.success('Turno reativado');
      }
      await queryClient.invalidateQueries({ queryKey: ['turnos'] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    } catch (err: unknown) {
      const apiMsg =
        (err as { response?: { data?: { error?: { message?: string }; message?: string } } })?.response?.data
          ?.error?.message ??
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(
        typeof apiMsg === 'string' && apiMsg ? apiMsg : statusConfirm.ativo ? 'Erro ao desativar' : 'Erro ao reativar',
      );
    } finally {
      setStatusConfirm(null);
    }
  };

  const toggleDia = (dia: string) => {
    setForm((f) => ({
      ...f,
      diasFuncionamento: f.diasFuncionamento.includes(dia)
        ? f.diasFuncionamento.filter((d) => d !== dia)
        : [...f.diasFuncionamento, dia],
    }));
  };

  const addSlot = (tipo: TipoSlotHorario) => {
    const last = template[template.length - 1];
    const inicio = last?.horaFim || form.horaEntradaOficial;
    let fim = last
      ? addMinutes(last.horaFim, tipo === 'AULA' ? 50 : 20)
      : addMinutes(form.horaEntradaOficial, tipo === 'AULA' ? 50 : 20);

    if (inicio >= form.horaSaidaOficial) {
      toast.warning('Não há mais espaço no horário oficial para novos slots.');
      return;
    }
    if (fim > form.horaSaidaOficial) fim = form.horaSaidaOficial;

    setTemplate(
      [
        ...template,
        {
          numeroAula: template.length + 1,
          horaInicio: inicio,
          horaFim: fim,
          tipo,
          descricao:
            tipo === 'AULA'
              ? `${template.filter((s) => s.tipo === 'AULA').length + 1}ª Aula`
              : 'Intervalo',
        },
      ].sort((a, b) => a.horaInicio.localeCompare(b.horaInicio)),
    );
  };

  const removeSlot = (index: number) => setTemplate(template.filter((_, i) => i !== index));

  const updateSlot = (index: number, field: keyof TemplateSlot, value: string) => {
    if (field === 'horaInicio' || field === 'horaFim') {
      if (value < form.horaEntradaOficial) {
        toast.warning(`Horário mínimo permitido: ${form.horaEntradaOficial}`);
        value = form.horaEntradaOficial;
      }
      if (value > form.horaSaidaOficial) {
        toast.warning(`Horário máximo permitido: ${form.horaSaidaOficial}`);
        value = form.horaSaidaOficial;
      }
    }
    setTemplate(
      template
        .map((s, i) => (i === index ? { ...s, [field]: value } : s))
        .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio)),
    );
  };

  const totalAulas = template.filter((s) => s.tipo === 'AULA').length;
  const totalIntervalos = template.filter((s) => s.tipo === 'INTERVALO').length;

  const columns: ColumnDef<Turno>[] = [
    {
      accessorKey: 'nome',
      header: 'Turno',
      cell: ({ row }) => {
        const t = row.original;
        return (
          <div style={{ minWidth: 0, opacity: t.ativo ? 1 : 0.4 }}>
            <div
              style={{
                fontWeight: 700,
                color: 'var(--text-main)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t.nome}
            </div>
            <div style={{ fontSize: '.78rem', color: '#2563eb', marginTop: 1 }}>
              {t.horaEntradaOficial || '-'} - {t.horaSaidaOficial || '-'}
            </div>
          </div>
        );
      },
    },
    {
      id: 'portoes',
      header: 'Portões',
      cell: ({ row }) => {
        const t = row.original;
        return (
          <span style={{ color: '#6b7280', fontSize: '0.8rem', opacity: t.ativo ? 1 : 0.4 }}>
            {t.horaAberturaPortoes || '-'} - {t.horaFechamentoPortoes || '-'}
          </span>
        );
      },
    },
    {
      id: 'tolerancia',
      header: 'Tolerância',
      cell: ({ row }) => (
        <span style={{ fontSize: '0.8rem', opacity: row.original.ativo ? 1 : 0.4 }}>
          {row.original.toleranciaAtrasoMin ?? 0} min
        </span>
      ),
    },
    {
      id: 'dias',
      header: 'Dias',
      cell: ({ row }) => {
        const t = row.original;
        const dias = t.diasFuncionamento ?? [];
        return (
          <div style={{ display: 'flex', gap: 2, opacity: t.ativo ? 1 : 0.4 }}>
            {DIAS_OPTIONS.map((d) => (
              <span
                key={d.value}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  background: dias.includes(d.value) ? '#dcfce7' : '#f3f4f6',
                  color: dias.includes(d.value) ? '#166534' : '#9ca3af',
                }}
              >
                {d.label}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'ativo',
      header: 'Status',
      cell: ({ row }) => (
        <span className={`badge ${row.original.ativo ? 'badge-active' : 'badge-inactive'}`}>
          {row.original.ativo ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      id: 'registro',
      header: 'Registro',
      cell: ({ row }) => {
        const t = row.original;
        if (!t.criadoEm) return <span className="ac-resp-registro ac-resp-registro--empty">—</span>;
        return <RegistroMeta criadoEm={t.criadoEm} atualizadoEm={t.atualizadoEm} />;
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const t = row.original;
        return (
          <div className="flex items-center">
            {/* Mobile: 3 pontinhos com menu de contexto */}
            <div className="flex md:hidden">
              <RowActionMenu
                items={[
                  {
                    label: 'Editar Turno',
                    icon: <Pencil size={15} />,
                    onClick: () => handlePermissionClick(canManage, () => openEdit(t), 'Você não tem permissão para editar turnos.'),
                    variant: !canManage ? 'disabled' : 'edit',
                  },
                  {
                    label: t.ativo ? 'Desativar Turno' : 'Reativar Turno',
                    icon: t.ativo ? <PowerOff size={15} /> : <RotateCcw size={15} />,
                    onClick: () => handlePermissionClick(canManage, () => requestToggleStatus(t), 'Você não tem permissão para alterar status de turnos.'),
                    variant: !canManage ? 'disabled' : (t.ativo ? 'danger' : 'success'),
                  },
                ]}
              />
            </div>

            {/* Desktop: Ações diretas */}
            <div className="hidden md:flex items-center gap-1">
              <button
                type="button"
                className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
                onClick={() => handlePermissionClick(canManage, () => openEdit(t), 'Você não tem permissão para editar turnos.')}
              >
                <Pencil size={15} />
              </button>
              <button
                type="button"
                className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : (t.ativo ? 'action-btn-danger' : 'action-btn-success')}`}
                title={!canManage ? 'Permissão restrita para alterar status' : (t.ativo ? 'Desativar' : 'Reativar')}
                onClick={() => handlePermissionClick(canManage, () => requestToggleStatus(t), 'Você não tem permissão para alterar status de turnos.')}
              >
                {t.ativo ? <PowerOff size={15} /> : <RotateCcw size={15} />}
              </button>
            </div>
          </div>
        );
      },
    },
  ];

  const turnosAtivos = turnos.filter((t) => t.ativo);
  const semTurnoAtivo = turnosAtivos.length === 0;

  return (
    <div
      className="admin-page ac-turnos-page"
      style={{ marginTop: -24, display: 'flex', flexDirection: 'column', gap: 0 }}
    >
      {!isLoading && (
        <SetupPageHero
          icon={Clock}
          etapa={2}
          titulo="Turnos Operacionais"
          descricao={
            semTurnoAtivo
              ? 'Turnos definem os horarios de funcionamento da escola: abertura de portoes, horario de entrada e saida, e o template de aulas e intervalos do dia.'
              : 'Cada turno define portões, horário oficial de entrada/saída, tolerância e o template de aulas. Use a lista abaixo para editar ou Criar Turno para adicionar outro período.'
          }
          dica={
            semTurnoAtivo
              ? 'A maioria das escolas tem 2 turnos: Manha (07:00-12:00) e Tarde (13:00-17:30). Comece pelo turno principal e adicione outros depois.'
              : undefined
          }
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}

      <div
        className="ac-admin-data-card"
        style={{
          background: 'var(--bg-card, white)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataToolbar
          busca={searchQuery}
          onBuscaChange={setSearchQuery}
          placeholder="Buscar turno..."
          actions={
            <button
              type="button"
              className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
              onClick={() => handlePermissionClick(canManage, openNew, 'Você não tem permissão para criar turnos.')}
              title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Criar novo turno'}
              style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              <Plus size={15} /> Criar Turno
            </button>
          }
          totalRecords={turnosFiltrados.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[6, 12, 24, 48]}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
          viewModes={['lista', 'cards']}
        />
        <div className="ac-admin-list-scroll" style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
          {viewMode === 'lista' ? (
            <DataTable
              columns={columns}
              data={turnosFiltrados}
              isLoading={isLoading}
              searchPlaceholder="Buscar turno..."
              showToolbar={false}
              pageSize={pageSize}
            />
          ) : (
            <div className="p-2 sm:p-4">
              {/* Mobile Compact Cards */}
              <div className="flex md:hidden flex-col gap-2">
                {turnosFiltrados.map((t) => {
                  const visual = getTurnoVisual(t.nome);
                  const aulasCount = t.horariosTemplate?.filter((h: any) => h.tipo === 'AULA').length || 0;
                  const recCount = t.horariosTemplate?.filter((h: any) => h.tipo === 'INTERVALO').length || 0;
                  const slotText = `${aulasCount} aula(s)${recCount > 0 ? ` + ${recCount} rec.` : ''}`;

                  return (
                    <div
                      key={t.id}
                      className="border border-[var(--border-color,#e5e7eb)] rounded-xl p-2.5 bg-[var(--bg-card,white)] shadow-sm hover:shadow transition-shadow flex items-center gap-3 relative overflow-hidden"
                      style={{ opacity: t.ativo ? 1 : 0.6 }}
                    >
                      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 4, background: visual.color }} />
                      
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          background: visual.bg,
                          fontSize: '1.2rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginLeft: 2,
                        }}
                      >
                        {visual.emoji}
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <div className="flex items-center justify-between gap-1.5">
                          <span className="font-bold text-xs text-[var(--text-main,#1e293b)] truncate">{t.nome}</span>
                          <span className={`badge ${t.ativo ? 'badge-active' : 'badge-inactive'} text-[9px] px-1.5 py-0.5 shrink-0`}>
                            {t.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-300 font-semibold">
                          {t.horaEntradaOficial || '-'} — {t.horaSaidaOficial || '-'}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {slotText} • Portões: {t.horaAberturaPortoes || '-'} às {t.horaFechamentoPortoes || '-'}
                        </div>
                      </div>

                      <div className="shrink-0 flex items-center">
                        <RowActionMenu
                          items={[
                            canManage
                              ? {
                                  label: 'Editar Turno',
                                  icon: <Pencil size={15} />,
                                  onClick: () => openEdit(t),
                                  variant: 'edit',
                                }
                              : null,
                            canManage
                              ? {
                                  label: t.ativo ? 'Desativar Turno' : 'Reativar Turno',
                                  icon: t.ativo ? <PowerOff size={15} /> : <RotateCcw size={15} />,
                                  onClick: () => requestToggleStatus(t),
                                  variant: t.ativo ? 'danger' : 'success',
                                }
                              : null,
                          ]}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Full Cards */}
              <div className="hidden md:grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
                {turnosFiltrados.map((t) => {
                  const visual = getTurnoVisual(t.nome);
                  const aulasCount = t.horariosTemplate?.filter((h: any) => h.tipo === 'AULA').length || 0;
                  const recCount = t.horariosTemplate?.filter((h: any) => h.tipo === 'INTERVALO').length || 0;
                  const slotText = `${aulasCount} ${aulasCount === 1 ? 'aula' : 'aulas'}${recCount > 0 ? ` + ${recCount === 1 ? 'recreio' : 'recreios'}` : ''}`;

                  return (
                    <div
                      key={t.id}
                      style={{
                        border: '1px solid var(--border-color, #e5e7eb)',
                        borderRadius: 16,
                        padding: '24px 16px',
                        background: 'var(--bg-card, white)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                        transition: 'all 0.25s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        opacity: t.ativo ? 1 : 0.8,
                      }}
                      className="ac-turno-card"
                    >
                      {/* Dynamic Top Stripe Accent */}
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: visual.color }} />

                      {/* Icon/Emoji Wrapper */}
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          background: visual.bg,
                          fontSize: '1.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 14,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
                        }}
                      >
                        {visual.emoji}
                      </div>

                      {/* Title */}
                      <h3
                        style={{
                          fontWeight: 700,
                          fontSize: '1.05rem',
                          color: 'var(--text-main)',
                          margin: '0 0 6px 0',
                        }}
                      >
                        {t.nome}
                      </h3>

                      {/* Times Slot Info */}
                      <div style={{ fontSize: '.84rem', fontWeight: 600, color: 'var(--text-main)', display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0' }}>
                        <span>{t.horaEntradaOficial || '-'}</span>
                        <span style={{ opacity: 0.4 }}>—</span>
                        <span>{t.horaSaidaOficial || '-'}</span>
                      </div>

                      {/* Slots description */}
                      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: '2px 0 10px 0' }}>
                        {slotText}
                      </div>

                      {/* Divider line */}
                      <div style={{ width: '80%', height: 1, background: 'var(--border-color, #e5e7eb)', margin: '8px 0 12px 0' }} />

                      {/* Portões & Tolerância Details */}
                      <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', lineHeight: 1.4, marginBottom: 16 }}>
                        <div>Portões: {t.horaAberturaPortoes || '-'} às {t.horaFechamentoPortoes || '-'}</div>
                        <div style={{ marginTop: 2 }}>Tolerância: {t.toleranciaAtrasoMin ?? 0} min</div>
                      </div>

                      {/* Status Badge */}
                      <span 
                        className={`badge ${t.ativo ? 'badge-active' : 'badge-inactive'}`}
                        style={{ marginBottom: 16 }}
                      >
                        {t.ativo ? 'Ativo' : 'Inativo'}
                      </span>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: 6, width: '100%', justifyContent: 'center', marginTop: 'auto' }}>
                        {canManage && (
                          <button 
                            type="button" 
                            className="action-btn" 
                            title="Editar" 
                            onClick={() => openEdit(t)}
                            style={{ width: 34, height: 34, borderRadius: 8 }}
                          >
                            <Pencil size={15} />
                          </button>
                        )}
                        {canManage && (
                          <button
                            type="button"
                            className={`action-btn ${t.ativo ? 'action-btn-danger' : 'action-btn-success'}`}
                            title={t.ativo ? 'Desativar' : 'Reativar'}
                            onClick={() => requestToggleStatus(t)}
                            style={{ width: 34, height: 34, borderRadius: 8 }}
                          >
                            {t.ativo ? <PowerOff size={15} /> : <RotateCcw size={15} />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {turnosFiltrados.length === 0 && (
                <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', padding: 8 }}>
                  Nenhum turno encontrado.
                </div>
              )}
            </div>
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={turnosFiltrados.length}
              pageSize={pageSize}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!statusConfirm}
        title={statusConfirm?.ativo ? 'Desativar turno' : 'Reativar turno'}
        message={
          statusConfirm
            ? statusConfirm.ativo
              ? `Deseja desativar o turno «${statusConfirm.nome}»? Ele deixa de estar disponível para turmas e para a grade até ser reativado.`
              : `Deseja reativar o turno «${statusConfirm.nome}»? Ele voltará a ficar disponível para turmas e para a grade.`
            : ''
        }
        cancelLabel="Cancelar"
        confirmLabel={statusConfirm?.ativo ? 'Desativar' : 'Reativar'}
        variant={statusConfirm?.ativo ? 'danger' : 'default'}
        onCancel={() => setStatusConfirm(null)}
        onConfirm={() => void confirmToggleStatus()}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          editing ? 'Editar turno' : modalStep === 'preset' ? 'Novo turno — escolha o modelo' : 'Novo turno'
        }
        size={modalStep === 'form' ? 'wide' : undefined}
        contentClassName={
          modalStep === 'form'
            ? editing
              ? 'ac-turno-modal ac-turno-modal--edit'
              : 'ac-turno-modal'
            : 'ac-turno-modal ac-turno-modal--preset'
        }
        footer={
          modalStep === 'form' ? (
            <>
              {!editing && (
                <button type="button" className="btn-cancel" onClick={() => setModalStep('preset')} style={{ marginRight: 'auto' }}>
                  ← Voltar
                </button>
              )}
              <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="btn-save" onClick={() => void handleSave()} disabled={savingTurno}>
                {savingTurno ? 'Salvando...' : 'Salvar'}
              </button>
            </>
          ) : undefined
        }
      >
        {modalStep === 'preset' ? (
          <TurnosPresetPicker onSelect={selectPreset} />
        ) : (
          <TurnosFormModal
            form={form}
            setForm={setForm}
            template={template}
            editing={editing}
            totalAulas={totalAulas}
            totalIntervalos={totalIntervalos}
            onToggleDia={toggleDia}
            onAddSlot={addSlot}
            onRemoveSlot={removeSlot}
            onUpdateSlot={updateSlot}
          />
        )}
      </Modal>
    </div>
  );
}
